import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const Contact: React.FC = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    hospital: '',
    role: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: '65404395-91b2-4f32-b66e-dd37908c3c56',
          subject: 'New Demo Request from SmartiStat Website',
          from_name: 'SmartiStat Website',
          to: 'contact@smartistat.com',
          message: `
New Demo Request:

Name: ${formData.name}
Email: ${formData.email}
Healthcare Facility: ${formData.hospital}
Role: ${formData.role}
Message: ${formData.message}
          `.trim()
        })
      });

      if (response.ok) {
        setFormSubmitted(true);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again later.');
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-10 lg:p-16 bg-gradient-to-br from-[#2e609b] to-[#1ba63f]">
              <h2 className="text-3xl font-bold text-white mb-6">Ready to transform your SPD & OR operations?</h2>
              
              <p className="text-white/90 mb-8">
                Schedule a personalized demo to see how our system can address your facility's specific challenges. Email us at{' '}
                <a href="mailto:contact@smartistat.com" className="text-white font-medium underline hover:no-underline">
                  contact@smartistat.com
                </a>
                {' '}or fill out the form.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-white/20 p-1 rounded-full mr-3 mt-1">
                    <Check size={16} className="text-white" />
                  </div>
                  <p className="text-white/90">See real-time instrument tracking in action</p>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/20 p-1 rounded-full mr-3 mt-1">
                    <Check size={16} className="text-white" />
                  </div>
                  <p className="text-white/90">Experience our intuitive dashboard and reporting</p>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/20 p-1 rounded-full mr-3 mt-1">
                    <Check size={16} className="text-white" />
                  </div>
                  <p className="text-white/90">Learn about our implementation process and support</p>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/20 p-1 rounded-full mr-3 mt-1">
                    <Check size={16} className="text-white" />
                  </div>
                  <p className="text-white/90">Get a customized ROI analysis for your facility</p>
                </div>
              </div>
              
              <div className="bg-white/10 p-4 rounded-lg">
                <p className="text-white font-medium">We typically respond within 24 hours</p>
              </div>
            </div>
            
            <div className="p-10 lg:p-16">
              {!formSubmitted ? (
                <>
                  <h3 className="text-2xl font-bold mb-6">Request Your Demo</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label htmlFor="name" className="block text-gray-700 mb-2">Full Name</label>
                      <input 
                        type="text" 
                        id="name" 
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ba63f] focus:border-transparent" 
                        placeholder="Jane Smith"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-gray-700 mb-2">Work Email</label>
                      <input 
                        type="email" 
                        id="email" 
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ba63f] focus:border-transparent" 
                        placeholder="jsmith@hospital.org"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="hospital" className="block text-gray-700 mb-2">Healthcare Facility</label>
                      <input 
                        type="text" 
                        id="hospital" 
                        value={formData.hospital}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ba63f] focus:border-transparent" 
                        placeholder="Metro General Hospital"
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="role" className="block text-gray-700 mb-2">Your Role</label>
                      <select 
                        id="role" 
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ba63f] focus:border-transparent"
                        required
                      >
                        <option value="">Select your role</option>
                        <option value="SPD Manager">SPD Manager</option>
                        <option value="OR Director">OR Director</option>
                        <option value="Surgical Services Director">Surgical Services Director</option>
                        <option value="Chief of Surgery">Chief of Surgery</option>
                        <option value="Hospital Administrator">Hospital Administrator</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div className="mb-6">
                      <label htmlFor="message" className="block text-gray-700 mb-2">Specific Interests or Questions</label>
                      <textarea 
                        id="message" 
                        rows={4} 
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ba63f] focus:border-transparent" 
                        placeholder="Tell us about your current challenges or areas of interest..."
                      ></textarea>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-[#2e609b] to-[#1ba63f] text-white py-3 px-6 rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity duration-300"
                    >
                      Request Demo
                      <ArrowRight size={18} className="ml-2" />
                    </button>
                  </form>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="bg-[#1ba63f]/10 p-4 rounded-full mb-6">
                    <Check size={40} className="text-[#1ba63f]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">Thank You!</h3>
                  <p className="text-gray-600 mb-6">
                    Your demo request has been received. One of our specialists will contact you within 24 hours to schedule your personalized demonstration.
                  </p>
                  <button 
                    className="text-[#2e609b] font-medium hover:text-[#1ba63f] transition-colors"
                    onClick={() => setFormSubmitted(false)}
                  >
                    Submit another request
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;